import React from "react";
import { useSiteContext } from "../../../../../context/SiteContext";
import { useEditCtaContext } from "../../../../../context/EditCtaContext";

const VerticalTemplateOneOneFour = ({
  titleText = "Choose your path",
  titleFont = "Helvetica",
  titleFontSize = "32",
  titleColor = "#FFFFFF",
  titleTextAlign = "center",
  titleIsBold = false,
  titleIsItalic = false,
  titleIsUnderline = false,

  subtitleText = "Pick what fits your goal today.",
  subtitleFont = "Helvetica",
  subtitleFontSize = "18",
  subtitleColor = "#FFFFFF",
  subtitleTextAlign = "center",
  subtitleIsBold = false,
  subtitleIsItalic = false,
  subtitleIsUnderline = false,
  subtitleBackgroundColor = "#000000",

  backgroundType = "color",
  backgroundColor = null,
  backgroundImage = null,

  buttonLabel = "Get Started",
  buttonFont = "Helvetica",
  buttonIsBold = false,
  buttonIsItalic = false,
  buttonIsUnderline = false,
  buttonLink = "",
  buttonFontSize = "18",
  buttonFontColor = "#FFFFFF",
  buttonBackgroundColor = "#00CED1",

  // Secondary Button
  secondaryButtonLabel = "Learn more",
  secondaryButtonFont = "Helvetica",
  secondaryButtonIsBold = false,
  secondaryButtonIsItalic = false,
  secondaryButtonIsUnderline = false,
  secondaryButtonLink = "",
  secondaryButtonFontSize = "18",
  secondaryButtonFontColor = "#FFFFFF",
  secondaryButtonBackgroundColor = "#FF4500",

  backgroundImagePreview,

  preview = false,
  inmeeting = false,
  onClose,
  position = "left",
}) => {
  const { meetingRole, setActiveElement } = useSiteContext();
  const { explicitBoleanCheck } = useEditCtaContext();
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 767;

  // Default cosmic background image
  const defaultBackgroundImage = "/oneoneFourbg.png";
  const finalBackgroundImage =
    backgroundImage || backgroundImagePreview || defaultBackgroundImage;

  return (
    <div
      className={`relative ${
        !inmeeting
          ? "h-full"
          : isMobile
          ? "h-full"
          : `!h-[91.7vh] w-72 mt-[12%] ${
              position === "left" ? "left-[-55px]" : "right-[-55px]"
            }`
      } ${
        isMobile && inmeeting
          ? position === "left" || position === "right"
            ? "" // Let it size naturally
            : "w-full" // Fill container for center/top/bottom
          : "w-72"
      } flex flex-col mx-auto justify-between items-center p-4 md:p-8 overflow-hidden`}
      style={{ backgroundColor }}
    >
      {/* Close Button */}
      {inmeeting && meetingRole === "moderator" && (
        <button
          className="absolute top-2 right-2 z-20 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center transition-colors"
          title="Close Banner"
          style={{ right: "8px", top: "8px" }}
          onClick={() => {
            if (meetingRole === "moderator") {
              onClose();
            } else {
              setActiveElement(null);
            }
          }}
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}

      {/* Background Image */}
      {finalBackgroundImage && finalBackgroundImage !== "null" && (
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${
              finalBackgroundImage instanceof File
                ? backgroundImagePreview
                : finalBackgroundImage
            })`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
      )}

      {/* Content Container */}
      <div className="relative h-full z-10 flex flex-col justify-center items-center w-full max-w-4xl mx-auto gap-6 md:gap-8">
        {/* Main Heading */}
        <h1
          className={`text-center ${
            explicitBoleanCheck(titleIsBold) ? "font-bold" : ""
          } ${explicitBoleanCheck(titleIsItalic) ? "italic" : ""} ${
            explicitBoleanCheck(titleIsUnderline) ? "underline" : ""
          }`}
          style={{
            color: titleColor,
            fontSize: `${titleFontSize}px`,
            fontFamily: titleFont,
            textAlign: titleTextAlign,
            lineHeight: "1.2",
            whiteSpace: "pre-line",
          }}
        >
          {titleText}
        </h1>

        {/* Subtitle with Background Box */}
        <div
          className={`inline-block px-4 py-3 rounded-lg ${
            explicitBoleanCheck(subtitleIsBold) ? "font-bold" : ""
          } ${explicitBoleanCheck(subtitleIsItalic) ? "italic" : ""} ${
            explicitBoleanCheck(subtitleIsUnderline) ? "underline" : ""
          }`}
          style={{
            color: subtitleColor,
            fontSize: `${subtitleFontSize}px`,
            fontFamily: subtitleFont,
            textAlign: subtitleTextAlign,
            backgroundColor: subtitleBackgroundColor,
            whiteSpace: "pre-line",
            opacity: 0.9,
          }}
        >
          {subtitleText}
        </div>

        {/* Buttons Container - Stacked Vertically */}
        <div className="flex flex-col gap-4 mt-4 w-full max-w-xs">
          {/* Secondary Button (Learn more) - Orange */}
          <button
            className={`px-6 py-3 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl ${
              secondaryButtonLink ? "cursor-pointer" : "cursor-default"
            }`}
            style={{
              backgroundColor: secondaryButtonBackgroundColor,
              color: secondaryButtonFontColor,
              fontSize: `${secondaryButtonFontSize}px`,
              fontFamily: secondaryButtonFont,
              fontWeight: explicitBoleanCheck(secondaryButtonIsBold)
                ? "bold"
                : "normal",
              fontStyle: explicitBoleanCheck(secondaryButtonIsItalic)
                ? "italic"
                : "normal",
              textDecoration: explicitBoleanCheck(secondaryButtonIsUnderline)
                ? "underline"
                : "none",
            }}
            onClick={() => {
              if (secondaryButtonLink) {
                window.open(secondaryButtonLink, "_blank");
              }
            }}
          >
            {secondaryButtonLabel}
          </button>

          {/* Primary Button (Get Started) - Teal */}
          <button
            className={`px-6 py-3 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl ${
              buttonLink ? "cursor-pointer" : "cursor-default"
            }`}
            style={{
              backgroundColor: buttonBackgroundColor,
              color: buttonFontColor,
              fontSize: `${buttonFontSize}px`,
              fontFamily: buttonFont,
              fontWeight: explicitBoleanCheck(buttonIsBold) ? "bold" : "normal",
              fontStyle: explicitBoleanCheck(buttonIsItalic)
                ? "italic"
                : "normal",
              textDecoration: explicitBoleanCheck(buttonIsUnderline)
                ? "underline"
                : "none",
            }}
            onClick={() => {
              if (buttonLink) {
                window.open(buttonLink, "_blank");
              }
            }}
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerticalTemplateOneOneFour;
